import axios from "axios";
import i18n from "../i18n";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Crédits IA : 402 → dialog global d'épuisement ; succès d'une action payante
// (marquée par une clé d'idempotence) → rafraîchissement immédiat du compteur.
api.interceptors.response.use(
  (res) => {
    const h = res.config?.headers || {};
    if (h["Idempotency-Key"] || h["idempotency-key"]) {
      window.dispatchEvent(new CustomEvent("credits:update"));
    }
    return res;
  },
  (err) => {
    // 402 "no_credits" → dialog crédits/quota ; les autres 402 (ex: paiement d'upgrade
    // refusé) restent gérés localement par l'écran appelant
    if (err?.response?.status === 402 && err?.response?.data?.detail === "no_credits") {
      window.dispatchEvent(new CustomEvent("credits:exhausted"));
    }
    return Promise.reject(err);
  }
);

export function fmtErr(e, fallback) {
  const fb = fallback || i18n.t("errors.generic");
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") {
    const key = `serverErrors.${detail}`;
    const translated = i18n.t(key);
    return translated !== key ? translated : detail;
  }
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (detail?.msg) return detail.msg;
  if (typeof e?.message === "string" && e.message.includes("IA")) return i18n.t("errors.aiFailed");
  return fb;
}

export const CATEGORIES = ["Prospect", "Client", "Facture", "Administratif", "Rendez-vous",
  "Fournisseur", "Newsletter", "SAV", "Urgent", "Autre"];

export const CATEGORY_COLORS = {
  Prospect: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400",
  Client: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  Facture: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400",
  Administratif: "bg-zinc-200 text-zinc-700 dark:bg-zinc-500/20 dark:text-zinc-300",
  "Rendez-vous": "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-400",
  Fournisseur: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  Newsletter: "bg-zinc-100 text-zinc-500 dark:bg-zinc-500/15 dark:text-zinc-400",
  SAV: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400",
  Urgent: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
  Autre: "bg-zinc-100 text-zinc-600 dark:bg-zinc-500/15 dark:text-zinc-400",
};

export const PRIORITY_META = {
  urgente: { label: "Urgente", dot: "bg-red-500", text: "text-red-600 dark:text-red-400" },
  haute: { label: "Haute", dot: "bg-orange-500", text: "text-orange-600 dark:text-orange-400" },
  normale: { label: "Normale", dot: "bg-blue-400", text: "text-muted-foreground" },
  basse: { label: "Basse", dot: "bg-zinc-300 dark:bg-zinc-600", text: "text-muted-foreground" },
};

export const LEAD_STAGES = [
  { key: "nouveau", label: "Nouveau" },
  { key: "contacte", label: "Contacté" },
  { key: "qualification", label: "Qualification" },
  { key: "proposition", label: "Proposition envoyée" },
  { key: "relance", label: "Relance" },
  { key: "gagne", label: "Gagné" },
  { key: "perdu", label: "Perdu" },
];

export function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return i18n.t("common.now");
  if (h < 24) return i18n.t("common.hoursAgo", { h });
  const d = Math.floor(h / 24);
  return i18n.t("common.daysAgo", { d });
}

export async function streamPost(path, body, onDelta, idemKey) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(idemKey ? { "Idempotency-Key": idemKey } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 402) window.dispatchEvent(new CustomEvent("credits:exhausted"));
    const err = await res.json().catch(() => ({}));
    const detail = typeof err.detail === "string" ? err.detail : null;
    if (detail) {
      const key = `serverErrors.${detail}`;
      const translated = i18n.t(key);
      throw new Error(translated !== key ? translated : detail);
    }
    throw new Error(i18n.t("errors.network"));
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let doneEvent = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim().replace(/^data: /, "");
      if (!line) continue;
      try {
        const obj = JSON.parse(line);
        if (obj.delta) onDelta(obj.delta);
        if (obj.done) doneEvent = obj;
        if (obj.error) throw new Error("IA_FAILED");
      } catch (e) {
        if (e.message === "IA_FAILED") throw new Error("IA_FAILED");
      }
    }
  }
  if (idemKey) window.dispatchEvent(new CustomEvent("credits:update"));
  return doneEvent;
}
